<template>
  <div 
    class="bug-modal-overlay" 
    @click="$emit('close-modal')"
    data-bug-reporter
  >
    <div class="bug-modal" @click.stop data-bug-reporter>
      <div class="bug-modal-header">
        <h3>Report a Bug</h3>
        <button @click="$emit('close-modal')" class="close-btn">&times;</button>
      </div>
      
      <div class="bug-modal-body">
        <CapturingState v-if="isCapturing" />
        
        <div v-else class="modal-content">
          <BugReportForm 
            :bug-report="bugReport"
            :console-log-count="consoleLogCount"
            :network-call-count="networkCallCount"
            :is-submitting="isSubmitting"
            @submit="$emit('submit-report')"
            @update-field="$emit('update-bug-report', $event.field, $event.value)"
            @cancel="$emit('close-modal')"
          />
          
          <!-- Screenshot Preview Section -->
          <ScreenshotPreview
            v-if="capturedScreenshot"
            :screenshot="capturedScreenshot"
            @edit="$emit('edit-screenshot')"
            @capture-new="$emit('submit-report')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CapturingState from './CapturingState.vue'
import BugReportForm from './BugReportForm.vue'
import ScreenshotPreview from './ScreenshotPreview.vue'

export default {
  name: 'BugReportModal',
  
  components: {
    CapturingState,
    BugReportForm,
    ScreenshotPreview
  },
  
  props: {
    isCapturing: {
      type: Boolean,
      default: false
    },
    isSubmitting: {
      type: Boolean,
      default: false
    },
    consoleLogCount: {
      type: Number,
      default: 0
    },
    networkCallCount: {
      type: Number,
      default: 0
    },
    bugReport: {
      type: Object,
      required: true
    },
    capturedScreenshot: {
      type: String,
      default: null
    }
  },
  
  emits: ['close-modal', 'submit-report', 'update-bug-report', 'edit-screenshot'],

  mounted() {
    // Mark this modal as bug reporter modal
    this.$el.setAttribute('data-bug-reporter', 'true')
    
    // Ensure proper focus management
    this.manageFocus()
  },

  methods: {
    manageFocus() {
      // Set focus to the modal for better accessibility and input focus
      this.$nextTick(() => {
        const firstInput = this.$el.querySelector('textarea, input, select')
        if (firstInput) {
          firstInput.focus()
        }
      })
    }
  }
}
</script>

<style scoped>
.bug-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 15000 !important; /* Higher than Vuetify's z-index */
  padding: 20px;
}

.bug-modal {
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 15001 !important;
}

.bug-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
  border-bottom: 1px solid #e5e7eb;
}

.bug-modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  z-index: 15002;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.bug-modal-body {
  padding: 24px;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: none;
}

/* Ensure inputs work properly inside the modal */
.bug-modal >>> input,
.bug-modal >>> textarea,
.bug-modal >>> select {
  position: relative;
  z-index: 1;
  background: white;
}

.bug-modal >>> input:focus,
.bug-modal >>> textarea:focus,
.bug-modal >>> select:focus {
  position: relative;
  z-index: 2;
}

@media (max-width: 640px) {
  .bug-modal-overlay {
    padding: 10px;
  }
  
  .bug-modal {
    max-width: 100%;
  }
}</style>